import React, { type FC, memo, useMemo } from "react";
import { motion } from "framer-motion";
import { cn } from "@heroui/react";

import { agentPanels } from "./agentPanels";
import { AgentReportView } from "./AgentReportView";
import styles from "./AgentMessageView.module.css";
import type { AgentMessage } from "./runner/types";
import { useTranslation } from "@domain/intl/useTranslation";

interface Props {
    message: AgentMessage;
    onPanelDone(id: string, result: "done" | "skipped"): void;
    /** Closes the modal; the report view calls it after leaving for the drafts. */
    onClose?(): void;
}

const entry = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.18 } };

const AgentMessageViewImpl: FC<Props> = ({ message, onPanelDone, onClose }) => {
    const { t } = useTranslation();
    const { kind, textId, values, labels, progress, panel, report, id } = message;

    const text = useMemo(() => {
        const resolved = Object.fromEntries(Object.entries(labels ?? {}).map(([key, labelId]) => [key, t(labelId)]));
        return t(textId, { ...values, ...resolved });
    }, [t, textId, values, labels]);

    const Panel = panel ? agentPanels[panel.type] : undefined;
    const handleDone = (result: "done" | "skipped") => onPanelDone(id, result);

    if (kind === "user") {
        return (
            <motion.div
                {...entry}
                data-testid="agent-user"
                className="max-w-[80%] self-end rounded-xl rounded-br-sm bg-accent px-3 py-1.5 text-accent-foreground"
            >
                {text}
            </motion.div>
        );
    }

    if (kind === "thinking") {
        return (
            <motion.div
                {...entry}
                data-testid="agent-thinking"
                className="flex items-center gap-2 self-start italic text-muted"
            >
                <span aria-hidden className={styles.dots}>
                    <span />
                    <span />
                    <span />
                </span>
                {text}
            </motion.div>
        );
    }

    return (
        <motion.div
            {...entry}
            data-testid={`agent-${kind}`}
            className={cn(
                "max-w-[85%] self-start rounded-xl rounded-bl-sm border border-separator bg-surface px-3 py-2 text-foreground",
                kind === "error" && "border-danger text-danger",
                kind === "warning" && "border-warning",
                kind === "skipped" && "text-muted",
            )}
        >
            {kind === "done" && (
                <span aria-hidden className="mr-1.5 font-semibold text-success">
                    ✓
                </span>
            )}
            {report ? (
                <>
                    <strong className="mb-2 block">{text}</strong>
                    <AgentReportView report={report} onClose={onClose} />
                </>
            ) : (
                !panel && text
            )}
            {progress && (
                <div className="mt-1.5">
                    <div
                        role="progressbar"
                        aria-label={text}
                        aria-valuemin={0}
                        aria-valuemax={progress.total}
                        aria-valuenow={progress.done}
                        className="h-1.5 overflow-hidden rounded-full bg-default"
                    >
                        <div
                            className="h-full bg-linear-to-r from-pink-500 to-indigo-400"
                            style={{
                                width: `${progress.total > 0 ? Math.min(100, (progress.done / progress.total) * 100) : 0}%`,
                            }}
                        />
                    </div>
                    <small className="text-muted">
                        {t("app.agent.progress", { done: progress.done, total: progress.total })}
                    </small>
                </div>
            )}
            {panel && (
                <div className="mt-2">
                    {Panel ? (
                        <Panel panel={panel} onDone={handleDone} />
                    ) : (
                        <div className="flex items-center gap-2 text-muted">
                            {t("app.agent.panelPlaceholder")}
                            <button
                                type="button"
                                className="rounded-full border border-separator px-3 py-1"
                                onClick={() => handleDone("skipped")}
                            >
                                {t("app.agent.panelSkip")}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export const AgentMessageView = memo(AgentMessageViewImpl);
